// Milestones — the rewards a user has gathered, and the next one they are walking toward.
//
// The server has had this since Momentum; the web app never showed it. Nothing here is sensitive
// (a milestone key and an entry count), so there is no DEK in this file.

import { apiGet } from './client';

export interface Milestone {
  key: string;
  title: string;
  description: string;
  /** Entry count that earns it. */
  milestone: number;
  earnedAt: string | null;
  /** Entries still to write. Only meaningful while unearned. */
  remaining: number;
}

export interface MilestonesView {
  entryCount: number;
  milestones: Milestone[];
  earnedCount: number;
  /** The nearest unearned milestone, or null once every one is gathered. */
  next: Milestone | null;
}

interface RewardDefRow {
  key: string;
  milestone?: number | null;
  title?: string | null;
  description?: string | null;
}

interface EarnedRow {
  milestone_key: string;
  earned_at?: string | null;
}

interface RewardsResponse {
  entryCount?: number;
  earned?: EarnedRow[];
  catalog?: RewardDefRow[];
}

/**
 * The full ladder, earned and unearned alike.
 *
 * Unearned milestones are shown veiled rather than hidden: knowing that "thirty days in a row"
 * exists and sits twelve days away is the pull. Hiding it would make the screen a trophy case for
 * people who are already finished.
 */
export async function fetchMilestones(signal?: AbortSignal): Promise<MilestonesView> {
  const res = await apiGet<RewardsResponse>('/api/rewards', signal);
  const entryCount = res.entryCount ?? 0;
  const earnedAt = new Map<string, string | null>();
  for (const row of res.earned ?? []) earnedAt.set(row.milestone_key, row.earned_at ?? null);

  const milestones: Milestone[] = (res.catalog ?? [])
    .map((def) => ({
      key: def.key,
      title: def.title || def.key,
      description: def.description || '',
      milestone: Number(def.milestone ?? 0),
      earnedAt: earnedAt.has(def.key) ? (earnedAt.get(def.key) ?? '') : null,
      remaining: Math.max(0, Number(def.milestone ?? 0) - entryCount),
    }))
    .sort((a, b) => a.milestone - b.milestone);

  const earnedCount = milestones.filter((m) => m.earnedAt !== null).length;
  const next = milestones.find((m) => m.earnedAt === null) ?? null;

  return { entryCount, milestones, earnedCount, next };
}
